/**
 * useWelcomeBonus — awards a one-time +50 pt welcome bonus to a newly signed-in
 * user and exposes a `justAwarded` flag so a celebration popup can appear once.
 *
 * Idempotency: guarded by `profiles.welcome_bonus_awarded` (server truth) AND
 * an `add_user_points` RPC call with a per-user reference id (server truth).
 */
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { logger } from "@/utils/logger";

export const WELCOME_BONUS_POINTS = 50;

export function useWelcomeBonus() {
  const { user } = useAuth();
  const [justAwarded, setJustAwarded] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;

    (async () => {
      try {
        const { data: profile } = await supabase
          .from("profiles")
          .select("welcome_bonus_awarded")
          .eq("id", user.id)
          .maybeSingle();

        if (cancelled) return;
        if (profile?.welcome_bonus_awarded) return;

        const { data, error } = await supabase.rpc("add_user_points", {
          p_user_id: user.id,
          p_points: WELCOME_BONUS_POINTS,
          p_action: "welcome_bonus" as any,
          p_reference_id: user.id,
          p_description: "Welcome to GoalDigger — starter bonus",
        });

        if (error) throw error;

        await supabase
          .from("profiles")
          .update({ welcome_bonus_awarded: true })
          .eq("id", user.id);

        const result = data as { success?: boolean; error?: string } | null;
        if (result?.success) {
          setJustAwarded(true);
          try {
            window.dispatchEvent(new CustomEvent("points-refresh"));
          } catch { /* non-fatal */ }
        }
      } catch (err) {
        logger.warn("welcome bonus award failed (non-critical)", err);
      }
    })();

    return () => { cancelled = true; };
  }, [user?.id]);

  return { justAwarded, dismiss: () => setJustAwarded(false) };
}
